import React, { useEffect, useState } from "react";
import { Lightbulb, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { learningApi } from "@/lib/apiClient";
import { useTranslation } from "@/context/LanguageContext";
import { toast } from "sonner";

export const ReflectionPanel = ({ docId }) => {
    const { t } = useTranslation();
    const [questions, setQuestions] = useState(null);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(false);

    const load = async (refresh = false) => {
        setLoading(true);
        try {
            const data = await learningApi.reflection(docId, refresh);
            setQuestions(data.questions || []);
        } catch (e) {
            toast.error(t("reflection.loadFailed"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(false); }, [docId]);

    if (loading && !questions) {
        return <div className="space-y-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-28 w-full rounded-2xl bg-surface-2" />)}</div>;
    }

    return (
        <div className="space-y-4" data-testid="reflection-panel">
            <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-t3">{t("reflection.intro")}</p>
                <Button variant="ghost" size="icon" className="shrink-0 text-t4 hover:text-t1" disabled={loading} onClick={() => load(true)}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                </Button>
            </div>
            {(questions || []).map((q, i) => (
                <div key={i} className="rounded-2xl border border-border bg-surface-1 p-4">
                    <div className="flex items-start gap-2">
                        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]"><Lightbulb className="h-3.5 w-3.5" /></div>
                        <p className="font-reading text-[15px] leading-relaxed text-t1">{q}</p>
                    </div>
                    <Textarea
                        data-testid={`reflection-answer-${i}`}
                        value={answers[i] || ""}
                        onChange={(e) => setAnswers((a) => ({ ...a, [i]: e.target.value }))}
                        placeholder={t("reflection.placeholder")}
                        className="mt-3 min-h-[80px] resize-none bg-surface-2 text-sm"
                    />
                </div>
            ))}
        </div>
    );
};
